import { post } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { getFileTypeFromBlob } from "@src/appV2/lib/files/utils";
import { useMutation, type UseMutationOptions } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

interface ContractorBadgePictureUploadParams {
  file: Blob;
  workerId: string;
}

const contractorBadgePicturePresignedUploadUrlRequestSchema = z.object({
  data: z.object({
    type: z.literal("contractor-badge-picture-presigned-upload-url"),
  }),
});

const contractorBadgePicturePresignedUploadUrlResponseSchema = z.object({
  data: z.object({
    type: z.literal("contractor-badge-picture-presigned-upload-url"),
    id: z.string().min(1),
    attributes: z.object({
      presignedUrl: z.string().min(1),
      workerId: z.string().min(1),
    }),
  }),
});

async function fetchContractorBadgePicturePresignedUrl(workerId: string) {
  const response = await post({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}/workers/${workerId}/contractor-badge-picture-presigned-upload-urls`,
    data: {
      data: {
        type: "contractor-badge-picture-presigned-upload-url",
      },
    },
    requestSchema: contractorBadgePicturePresignedUploadUrlRequestSchema,
    responseSchema: contractorBadgePicturePresignedUploadUrlResponseSchema,
  });

  return response.data;
}

export function useContractorBadgePictureUpload(
  options: UseMutationOptions<string, Error, ContractorBadgePictureUploadParams> = {}
) {
  return useMutation({
    mutationFn: async ({ file, workerId }) => {
      const { data } = await fetchContractorBadgePicturePresignedUrl(workerId);
      const { presignedUrl, workerId: responseWorkerId } = data.attributes;
      const fileType = await getFileTypeFromBlob(file);
      const headers = fileType ? { "Content-Type": fileType.contentType } : undefined;

      await axios.put(presignedUrl, file, ...(headers ? [{ headers }] : []));

      logEvent(APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_IMAGE_UPLOAD_SUCCESS, {
        workerId: responseWorkerId,
      });

      return data.id;
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.INDEPENDENT_CONTRACTOR_BADGE_IMAGE_UPLOAD_FAILURE,
    },
    ...options,
  });
}
